import { FC, useCallback, useMemo } from 'react';
import { TimeWindow } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { taskWidgetTrackingIds } from '@frontend/task-center-components';
import { Listbox, PopoverMenu, Text, usePopoverMenu, Button, useListboxState } from '@frontend/design-system';
import { useDashboardWidgetStore } from '../../../provider/dashboard-provider/dashboard-widget.provider';
import { timeRangeSelectorButton } from './styles';

interface TimeRangeProps {
  isWidthSmall: boolean;
}

export const TimeRangeSelector: FC<TimeRangeProps> = ({ isWidthSmall }) => {
  const { t } = useTranslation('task-center');

  const { timeWindow, setTimeWindow } = useDashboardWidgetStore(['timeWindow', 'setTimeWindow']);

  const { getMenuProps, getTriggerProps, close } = usePopoverMenu({
    placement: 'bottom-end',
  });
  const { labelId } = useListboxState(timeWindow);

  const TIME_RANGE_OPTIONS: { value: TimeWindow; labelKey: string }[] = useMemo(
    () => [
      // the labelKey uses dashboard as ns, the same keys are shared within dashboard ns
      { value: TimeWindow['TIME_WINDOW_TODAY'], labelKey: t('Today', { ns: 'dashboard' }) },
      { value: TimeWindow['TIME_WINDOW_YESTERDAY'], labelKey: t('Yesterday', { ns: 'dashboard' }) },
      { value: TimeWindow['TIME_WINDOW_LAST_7_DAYS'], labelKey: t('Last 7 days', { ns: 'dashboard' }) },
      { value: TimeWindow['TIME_WINDOW_LAST_14_DAYS'], labelKey: t('Last 14 days', { ns: 'dashboard' }) },
      { value: TimeWindow['TIME_WINDOW_LAST_30_DAYS'], labelKey: t('Last 30 days', { ns: 'dashboard' }) },
    ],
    [t]
  );

  const onSelect = useCallback(
    (value: string | string[]) => {
      setTimeWindow(value as TimeWindow);
      close();
    },
    [close, setTimeWindow]
  );

  const selectedLabel = TIME_RANGE_OPTIONS.find((opt) => opt.value === timeWindow)?.labelKey ?? '';

  return (
    <>
      <Button
        variant='tertiary'
        aria-label={t('Select Time Range')}
        css={timeRangeSelectorButton(isWidthSmall)}
        trackingId={taskWidgetTrackingIds.timeRangePicker}
        {...getTriggerProps()}
      >
        <Icon name='clock-small' color='default' />
        <Text id={labelId} weight='bold' size='small'>
          {selectedLabel}
        </Text>
      </Button>

      <PopoverMenu {...getMenuProps()}>
        <Listbox labelId={labelId} value={timeWindow} onSelect={onSelect}>
          {TIME_RANGE_OPTIONS.map((option) => (
            <Listbox.Option
              key={option.value}
              value={option.value}
              trackingId={`${taskWidgetTrackingIds.timeRangePrefix}${option.value}`}
            >
              {option.labelKey}
            </Listbox.Option>
          ))}
        </Listbox>
      </PopoverMenu>
    </>
  );
};
